import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* Shared editorial resource card */
const ResourceCard = ({ item, testId }) => {
  const cardClass =
    "group flex flex-col overflow-hidden rounded-xl border border-[hsl(var(--primary))]/12 bg-card transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const inner = (
    <>
      {/* Card artwork */}
      <span className="relative block overflow-hidden p-4 sm:p-5">
      <img
        src={item.img}
        alt={item.alt}
        className="wfii-photo aspect-[16/10] w-full rounded-lg object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        loading="lazy"
      />
    </span>
    {/* Body */}
    <span className="flex flex-1 flex-col p-7 pt-0 sm:p-8 sm:pt-0">
      <span className="flex items-center justify-between">
        <span className="flex items-center gap-2.5">
          <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
            {item.type}
          </span>
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
          {item.time}
        </span>
      </span>
      <span className="mt-5 text-xl font-semibold leading-snug tracking-[-0.01em] text-foreground transition-colors group-hover:text-[hsl(var(--wfii-teal))] sm:text-[1.3rem]">
        {item.titleJsx || item.title}
      </span>
      <span className="mt-4 flex flex-wrap gap-2">
        {item.tags.map((t) => (
          <span
            key={t}
            className="rounded-full bg-[hsl(var(--primary))]/[0.06] px-3 py-1 text-xs font-medium text-foreground/70"
          >
            {t}
          </span>
        ))}
      </span>
      <span className="mt-8 flex flex-1 items-end justify-end">
        <ArrowRight
          className="h-4 w-4 text-muted-foreground transition-all duration-200 group-hover:translate-x-1 group-hover:text-[hsl(var(--accent))]"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </span>
    </span>
    </>
  );
  if (item.link) {
    return (
      <Link to={item.link} className={cardClass} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return (
    <a href="#" onClick={inert} className={cardClass} data-testid={testId}>
      {inner}
    </a>
  );
};

/* ================= Block 1 — Hero ================= */

const ResourcesHero = () => (
  <section data-testid="resources-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="grid gap-12 lg:grid-cols-[1fr_520px] lg:items-center lg:gap-16">
        {/* Left — editorial text */}
        <div className="lg:self-start">
          <div className="flex items-center justify-center gap-3 sm:justify-start" aria-hidden="true">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 sm:hidden" />
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
              WFII Resources
            </span>
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))] sm:hidden" />
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 sm:hidden" />
          </div>
          <h1 className="font-serif-editorial mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
            Resources and Guides
          </h1>
          <p className="mt-8 text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
            WFII resources make digital finance easier to understand{" "}
            <br className="hidden lg:block" aria-hidden="true" />
            in practical business terms.
          </p>
          <p className="mt-4 text-center text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-left">
            Explore guides, articles, webinars, glossaries, and checklists on
            stablecoins, payments, accounting, compliance, and financial
            infrastructure.
          </p>
        </div>
        {/* Right — editorial photo */}
        <div>
          <div className="overflow-hidden rounded-2xl shadow-[0_32px_64px_-32px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10">
            <img
              src="/resources-hero.jpg"
              alt="Classical building facade with tall stone columns"
              className="wfii-photo aspect-[4/3] w-full object-cover"
              loading="eager"
              data-testid="resources-hero-image"
            />
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — Start with the fundamentals ================= */

const FUNDAMENTALS = [
  {
    type: "Guide",
    title: "Understanding Stablecoins in Business Finance",
    titleJsx: (<>Understanding Stablecoins{" "}<br className="hidden lg:block" />in Business Finance</>),
    link: "/resources/understanding-stablecoins-in-business-finance",
    time: "14 min",
    tags: ["Stablecoins", "Payments"],
    img: "/resource-guide.jpg",
    alt: "Modern office building entrance with revolving doors",
  },
  {
    type: "Guide",
    title: "Digital Asset Records: What Finance Teams Should Know",
    link: "/resources/digital-asset-records-and-reporting",
    tags: ["Digital Assets", "Accounting"],
    time: "9 min",
    img: "/resource-checklist.jpg",
    alt: "Finance team members collaborating around a conference table with laptops",
  },
  {
    type: "Glossary",
    title: "25 Essential Digital Finance Terms",
    link: "/resources/glossary-25-essential-digital-finance-terms",
    tags: ["Digital Assets", "Accounting", "Stablecoins", "Payments"],
    time: "6 min",
    img: "/resource-glossary.jpg",
    alt: "Aerial view of a modern financial conference exhibition hall",
  },
];

const Fundamentals = () => (
  <section data-testid="resources-fundamentals-section" className="!mt-[50px] lg:!mt-[120px]">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center lg:max-w-none">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Start with the Fundamentals
        </h2>
        <p className="mx-auto mt-6 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none">
          These fundamental resources introduce the key concepts, terminology,
          and business applications{" "}
          <br className="hidden lg:block" aria-hidden="true" />
          behind digital money, stablecoins, payments, and modern financial
          workflows.
        </p>
      </div>
      <div className="mt-[38px] grid gap-6 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
        {FUNDAMENTALS.map((item, i) => (
          <ResourceCard
            key={item.title}
            item={item}
            testId={`resources-fundamentals-card-${i + 1}`}
          />
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 3 — Explore more ================= */

const MORE_RESOURCES = [
  {
    type: "Article",
    title: "Why Finance Teams Are Paying Attention to Stablecoins",
    link: "/resources/why-finance-teams-are-paying-attention-to-stablecoins",
    tags: ["Stablecoins", "Payments"],
    time: "5 min",
    img: "/resource-explore-1.jpg",
    alt: "Audience watching a stablecoins presentation in a seminar room",
  },
  {
    type: "Article",
    title: "How Business Payments Are Changing",
    titleJsx: (<>How Business Payments{" "}<br className="hidden lg:block" />Are Changing</>),
    link: "/resources/how-business-payments-are-changing",
    tags: ["Payments"],
    time: "4 min",
    img: "/resource-explore-2.jpg",
    alt: "Finance professionals discussing partnership opportunities around a laptop",
  },
  {
    type: "Article",
    title: "Understanding Modern Financial Infrastructure",
    link: "/resources/understanding-modern-financial-infrastructure",
    tags: ["Financial Infrastructure", "Payments"],
    time: "6 min",
    img: "/resource-explore-3.jpg",
    alt: "Upward view of modern financial district skyscrapers against a cloudy sky",
  },
];

const ExploreMore = () => (
  <section data-testid="resources-explore-section">
    <Container className="">
      <h2 className="text-center text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        Explore More
      </h2>
      <div className="mt-[33px] grid gap-6 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
        {MORE_RESOURCES.map((item, i) => (
          <ResourceCard
            key={item.title}
            item={item}
            testId={`resources-explore-card-${i + 1}`}
          />
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 4 — Final CTA ================= */

const ResourcesFinalCta = () => (
  <section data-testid="resources-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Continue Building{" "}
            <br className="lg:hidden" />
            Your Expertise
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Move from fundamental concepts to structured learning with WFII
              programs designed for finance professionals working with digital
              money, payments, accounting workflows, and compliance.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="resources-final-cta-explore-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function ResourcesPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <ResourcesHero />
          <Fundamentals />
          <ExploreMore />
          <ResourcesFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
